import { useMemo } from 'react'
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Flex,
  Input,
  Row,
  Segmented,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import type { TagProps } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { DownloadOutlined } from '@ant-design/icons'
import dayjs, { type Dayjs } from 'dayjs'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { EmptyState } from '@/shared/ui/EmptyState'
import { PageHeader } from '@/shared/ui/PageHeader'
import { formatDate, formatDateTime, formatDuration, formatNumber, utcTooltip } from '@/shared/lib/format'
import { tenantToday } from '@/shared/lib/dayRange'
import {
  applyLocalList,
  byNumber,
  byText,
  localSortChange,
  sortColumn,
  useLocalListState,
} from '@/shared/lib/localList'
import { relativeFromNow } from '@/features/session/relativeTime'
import { ReportNoticesAlert } from '@/features/attendance/ReportNoticesAlert'
import {
  DAY_STATUSES,
  downloadAttendanceExport,
  fetchDailyReport,
  fetchEmployeeOptions,
  fetchLastSeen,
  fetchMonthlyReport,
  fetchPresent,
  type DailyReportRow,
  type DayStatus,
  type DirectionSource,
  type LastSeenRow,
  type MonthlyReportRow,
  type PresentRow,
} from '@/features/attendance/attendanceApi'

type View = 'daily' | 'monthly'

/**
 * URL'dagi sanani `Dayjs` ga o'giradi.
 *
 * ⚠️ Yaroqsiz qiymat (qo'lda tahrirlangan havola) sahifani YIQITMAYDI
 * va jimgina «bugun» ga qaytadi: hisobot ekranida bo'sh oq sahifa
 * ko'rsatish operatorga hech narsa aytmasdi.
 */
function parseDay(value: string | null, fallback: () => Dayjs): Dayjs {
  if (value === null) return fallback()
  const parsed = dayjs(value)
  return parsed.isValid() ? parsed : fallback()
}

/**
 * RANG — HOLATNING MA'NOSIGA qarab.
 *
 *   · `COMPLETE`   YASHIL  — kelish ham, ketish ham bor. Ishlagan vaqt MA'LUM
 *   · `INCOMPLETE` SARIQ   — biri yo'q. ⚠️ QIZIL EMAS: bu odamning aybi
 *                            emas va ko'pincha umuman muammo emas —
 *                            terminal chiqishda ishlatilmagan bo'lishi
 *                            mumkin, yoki bu tungi smena. Sariq "diqqat
 *                            qiling", qizil esa "xato" degani bo'lardi
 *   · `ABSENT`     KULRANG — birorta yozuv yo'q. Bu ham xato emas: dam
 *                            olish kuni, ta'til, kasallik. Hisobot
 *                            ularning qaysi biri ekanini BILMAYDI va
 *                            bilgandek ko'rsatmasligi kerak
 */
const STATUS_COLOR: Record<DayStatus, TagProps['color']> = {
  COMPLETE: 'green',
  INCOMPLETE: 'gold',
  ABSENT: 'default',
}

/**
 * Davomat hisoboti — kunlik va oylik kesim.
 *
 * <h2>⚠️ SAHIFANING ASOSIY QOIDASI: "0 SOAT" DEB YOZILMAYDI</h2>
 * Tugallanmagan kunda (kelish bor, ketish yo'q) ishlagan vaqt
 * <b>NOMA'LUM</b>. Uni nol deb ko'rsatish — o'ylab topilgan raqam, va bu
 * hisobot aynan shu raqam bo'yicha ish haqi hisoblanadigan hujjat. Odam
 * kun bo'yi ishlagan bo'lishi mumkin, shunchaki chiqishda terminalga
 * yaqinlashmagan.
 *
 * Bu qoida IKKI QATLAMDA bajariladi: backend tugallanmagan kunda
 * `workedMinutes` ni `null` qiladi, `attendanceApi` esa uni yana bir bor
 * filtrlaydi. Takror ataylab — bitta qatlamning kelajakdagi o'zgarishi
 * o'ylab topilgan raqamni ekranga chiqarmasligi kerak.
 *
 * <h2>⚠️ OYLIK KESIM — IKKI REJIM, VA ULAR BOSHQA SAVOL</h2>
 * Qatorlar KUNLAR (xodimlar emas — bu avval noto'g'ri tushunilgan edi):
 *   · xodim tanlanmagan → butun tashkilotning oyi, kun-baday.
 *     "Qaysi kunlari kelganlar kam bo'lgan?"
 *   · xodim tanlangan   → o'sha xodimning oyi, kun-baday.
 *     "Alisher shu oyda qaysi kunlari kelmagan?"
 * Ustunlar ham shunga qarab O'ZGARADI: umumiy rejimda "necha xodim",
 * shaxsiy rejimda "kelish/ketish/ishlagan vaqt". Bitta ustunlar
 * to'plamini ikkalasiga ham qo'llash yarmi doim bo'sh jadval berardi.
 *
 * <h2>⚠️ OGOHLANTIRISHLAR JADVAL YONIDA</h2>
 * `truncated`, `multiZone`, `notes` va `unidentifiedDeniedCount` —
 * hammasi `ReportNoticesAlert` orqali ko'rinadi. Sabab o'sha komponentda:
 * ularsiz hisobot haqiqiydan ko'ra ishonchliroq ko'rinardi.
 *
 * <h2>Nega `DataTable` emas</h2>
 * Hisobot SERVERDA sahifalanmaydi: u bir kun (yoki bir oy) uchun butun
 * natijani qaytaradi. `AttendanceReportController` `page`/`size` ni
 * UMUMAN olmaydi (`date`/`month`, `employeeId`, `deviceId` — hammasi
 * shu). `DataTable` esa server tomon sahifalashni talab qiladi
 * (`fetchPage(state)`), ya'ni uni bu yerda ishlatish mavjud bo'lmagan
 * imkoniyatni taqlid qilardi. Butun ro'yxatni olib mijozda kesish ham
 * ATAYLAB qilinmadi (egasi qarori, 2026-08-20).
 *
 * <h2>⚠️ `created desc` BU YERDA QO'LLANMAYDI — VA BU ONGLI QAROR</h2>
 * Hisobot qatorlarida yaratilish vaqti UMUMAN yo'q: kunlik kesimda
 * qator — XODIM, oylik kesimda — KUN. "Oxirgi qo'shilgan birinchi"
 * degan tushuncha ikkalasida ham ma'nosiz, va vaqtni o'ylab topib
 * saralash yolg'on tartib bo'lardi. Standart tartib SERVERNIKI
 * qoladi (kunlik — xodimlar bo'yicha, oylik — kalendar bo'yicha,
 * ya'ni hisobot o'qiladigan tabiiy tartib), boshqasi esa ustun
 * sarlavhasidan tanlanadi.
 */
export function AttendanceReportPage() {
  const { t } = useTranslation()

  /**
   * ⚠️ EKRAN HOLATI MANZILDA (`useTableUrlState`), `useState` da EMAS.
   *
   * Hisobotda eng ko'p ulashiladigan narsa — AYNAN shu kesim ("mana
   * shu kunga qara"). `useState` da u havolaga tushmasdi va sahifa
   * yangilanganda bugungi kunga qaytardi.
   *
   * ⚠️ `view`/`day`/`month`/`employee` — KONTEKST, filtr emas: ular
   * savolning o'zini belgilaydi, ro'yxatni toraytirmaydi. Shuning
   * uchun `narrowingKeys` da faqat `status` va `registry` bor —
   * aks holda «Tozalash» tugmasi doim yonib turardi va sana ham
   * tozalanib ketardi.
   */
  const list = useLocalListState({ narrowingKeys: ['status', 'registry'] })

  const view: View = list.filter('view') === 'monthly' ? 'monthly' : 'daily'
  const setView = (next: View) => list.setFilter('view', next === 'monthly' ? 'monthly' : null)

  // ⚠️ Boshlang'ich "bugun" ham TENANT kalendarida. Quyidagi izoh
  // `toISOString()` xatosini to'g'ri tasvirlaydi, lekin `dayjs()` ning
  // O'ZI ham brauzer kunini berardi — ya'ni yarim tundan keyingi yarim
  // soatda sahifa ERTANGI kun bilan ochilardi va operator buni sezmasdi.
  const day = parseDay(list.filter('day'), tenantToday)
  const month = parseDay(list.filter('month'), tenantToday)
  /** `null` = UMUMIY rejim (butun tashkilot) */
  const employeeId = list.filter('employee')

  /*
    ⚠️ SANA TENANT ZONASIDA YASALADI, UTC'da EMAS. `toISOString()` bu
    yerda XATO bo'lardi: server `?date=YYYY-MM-DD` kutadi va kun
    chegarasini o'zi hisoblaydi. Brauzer UTC'ga o'girsa, UTC+5 da
    ertalabki soatlarda sana BIR KUN ORQAGA surilardi — operator
    "bugun" tanlab, kechagi hisobotni ko'rardi va farqni sezmasdi.
  */
  const dateParam = day.format('YYYY-MM-DD')
  const monthParam = month.format('YYYY-MM')

  const employees = useQuery({
    queryKey: queryKeys.attendance.employeeOptions,
    queryFn: () => fetchEmployeeOptions(),
    staleTime: 5 * 60_000,
  })

  const daily = useQuery({
    queryKey: queryKeys.attendance.report.daily(dateParam),
    queryFn: ({ signal }) => fetchDailyReport(dateParam, signal),
    enabled: view === 'daily',
  })

  const monthly = useQuery({
    // ⚠️ `employeeId` KALITGA kiradi: xodim almashtirilganda YANGI so'rov
    // ketishi kerak. Umumiy kalit bo'lsa oldingi xodimning oyi ekranda
    // qolardi va ustunlar mos kelgani uchun buni sezish qiyin bo'lardi.
    queryKey: queryKeys.attendance.report.monthly(monthParam, employeeId),
    queryFn: ({ signal }) => fetchMonthlyReport(monthParam, employeeId, signal),
    enabled: view === 'monthly',
  })

  const present = useQuery({
    queryKey: queryKeys.attendance.report.present,
    queryFn: ({ signal }) => fetchPresent(signal),
    // 60 soniya: hodisalar fon jarayoni orqali tortiladi, undan tez
    // so'rash yangi ma'lumot bermaydi (`AttendanceDevicesPage` bilan
    // bir xil mulohaza).
  // ⚠️ Avtoyangilanish OLIB TASHLANDI (egasi qarori 2026-08-20):
  //    u faqat bosh sahifada qoladi. Qolgan ekranlarda u backendga
  //    doimiy yuk berardi, holbuki operator sahifani ochiq qoldirib
  //    boshqa ish bilan band bo'ladi. Yangilash QO'LDA — tugma bor.
  })

  const lastSeen = useQuery({
    queryKey: queryKeys.attendance.report.lastSeen,
    queryFn: ({ signal }) => fetchLastSeen(signal),
  // ⚠️ Avtoyangilanish OLIB TASHLANDI (egasi qarori 2026-08-20):
  //    u faqat bosh sahifada qoladi. Qolgan ekranlarda u backendga
  //    doimiy yuk berardi, holbuki operator sahifani ochiq qoldirib
  //    boshqa ish bilan band bo'ladi. Yangilash QO'LDA — tugma bor.
  })

  const dailyRows = useMemo(() => daily.data?.rows ?? [], [daily.data])
  const monthlyRows = useMemo(() => monthly.data?.rows ?? [], [monthly.data])
  /** Oylik: xodim tanlanganmi — ustunlar to'plami shunga bog'liq */
  const personalMonth = employeeId !== null

  /*
    ⚠️ QIDIRUV VA FILTR — MIJOZDA, chunki hisobot endpointi ularni
    UMUMAN olmaydi: `AttendanceReportController#daily` faqat `date`,
    `employeeId` va `deviceId` ni, `#monthly` esa `month` va
    `employeeId` ni qabul qiladi. `q`, `status`, `page`, `size` yo'q.

    Va bu yolg'on tartib bermaydi: javob BIR KUN (yoki bir oy) uchun
    TO'LIQ keladi, ya'ni saralanadigan massiv butun hisobotning o'zi.
  */
  const visibleDaily = useMemo(
    () =>
      applyLocalList(dailyRows, {
        search: list.search,
        searchFields: (row) => [row.displayName, row.employeeNo],
        filters: {
          status: (row, value) => row.status === value,
          registry: (row, value) => (value === 'missing' ? !row.inRegistry : row.inRegistry),
        },
        filterState: list.state.filters,
        comparators: {
          employee: byText((row) => row.displayName ?? row.employeeNo),
          // ⚠️ `workedMinutes === null` — NOMA'LUM, nol emas. Uni `0`
          //    bilan almashtirib saralash tugallanmagan kunlarni
          //    "hech ishlamagan" bo'lib eng pastga tashlardi.
          worked: (a, b) => {
            if (a.workedMinutes === null && b.workedMinutes === null) return 0
            if (a.workedMinutes === null) return -1
            if (b.workedMinutes === null) return 1
            return a.workedMinutes - b.workedMinutes
          },
          denied: byNumber((row) => row.deniedCount ?? 0),
        },
        sort: list.sort,
      }),
    [dailyRows, list.search, list.state.filters, list.sort],
  )

  const visibleMonthly = useMemo(
    () =>
      applyLocalList(monthlyRows, {
        search: '',
        filters: {
          // ⚠️ Faqat SHAXSIY oylik kesimda ma'noli: umumiy rejimda
          //    qator — butun tashkilotning kuni va uning yagona holati
          //    yo'q (`MonthlyReportRow.status` u yerda yig'ma qiymat).
          status: (row, value) => (personalMonth ? row.status === value : true),
        },
        filterState: list.state.filters,
        comparators: {
          date: byText((row) => row.date),
          employeeCount: byNumber((row) => row.employeeCount ?? 0),
          absent: byNumber((row) => row.absentCount ?? 0),
          // ⚠️ `null` — NOMA'LUM ish vaqti; nolga aylantirilmaydi.
          worked: (a, b) => {
            if (a.workedMinutes === null && b.workedMinutes === null) return 0
            if (a.workedMinutes === null) return -1
            if (b.workedMinutes === null) return 1
            return a.workedMinutes - b.workedMinutes
          },
        },
        sort: list.sort,
      }),
    [monthlyRows, list.state.filters, list.sort, personalMonth],
  )

  const dailyColumns = useMemo<ColumnsType<DailyReportRow>>(
    () => [
      {
        key: 'employee',
        title: t('attendance.report.employee'),
        ...sortColumn(list.sort, 'employee'),
        render: (_, row) => (
          <Space direction="vertical" size={0}>
            <Space size={6}>
              <Typography.Text>{row.displayName ?? t('attendance.report.noName')}</Typography.Text>
              {/* ⚠️ Reyestrda yo'q odam — tekshiruvda birinchi
                  qidiriladigan qator. Belgisiz u oddiy xodimdan
                  farq qilmasdi. */}
              {row.inRegistry ? null : (
                <Tooltip title={t('attendance.report.notInRegistryHint')}>
                  <Tag color="gold">{t('attendance.report.notInRegistry')}</Tag>
                </Tooltip>
              )}
            </Space>
            <Typography.Text code style={{ fontSize: 12 }}>
              {row.employeeNo}
            </Typography.Text>
          </Space>
        ),
      },
      {
        key: 'firstIn',
        title: t('attendance.report.firstIn'),
        width: 160,
        render: (_, row) => (
          <TimeCell iso={row.firstInAt} local={row.firstInLocal} device={row.firstInDevice} />
        ),
      },
      {
        key: 'lastOut',
        title: t('attendance.report.lastOut'),
        width: 160,
        render: (_, row) => (
          <TimeCell iso={row.lastOutAt} local={row.lastOutLocal} device={row.lastOutDevice} />
        ),
      },
      {
        key: 'worked',
        title: t('attendance.report.worked'),
        width: 190,
        ...sortColumn(list.sort, 'worked'),
        render: (_, row) => <WorkedCell status={row.status} workedMinutes={row.workedMinutes} />,
      },
      {
        key: 'status',
        title: t('attendance.report.status'),
        // ⚠️ Kengaytirildi: qatorda endi uchtagacha yorliq bo'lishi
        // mumkin (holat + manba + juftlanmagan). Sig'masa oxirgisi
        // jimgina kesilib qolardi va u aynan eng muhimi.
        width: 300,
        render: (_, row) => (
          <StatusCell
            status={row.status}
            nightShiftSuspected={row.nightShiftSuspected}
            note={row.note}
            directionSource={row.directionSource}
            unpaired={row.unpaired}
          />
        ),
      },
      {
        /*
          ⚠️ RAD ETILGAN URINISHLAR USTUNI — bu qatorning ISHONCHLILIK
          o'lchovi. Xodim "kelmagan" deb belgilangan, lekin uning
          nomiga rad etilgan urinishlar yozilgan bo'lsa — u KELGAN,
          shunchaki terminal uni tanimagan. Bu ustunsiz bunday holat
          oddiy "kelmagan" bo'lib ko'rinardi.
        */
        key: 'denied',
        title: t('attendance.report.denied'),
        width: 120,
        ...sortColumn(list.sort, 'denied'),
        render: (_, row) =>
          row.deniedCount ? (
            <Tooltip title={t('attendance.report.deniedHint')}>
              <Tag color={row.status === 'ABSENT' ? 'red' : 'default'}>
                {formatNumber(row.deniedCount)}
              </Tag>
            </Tooltip>
          ) : (
            <Typography.Text type="secondary">{t('common.dash')}</Typography.Text>
          ),
      },
    ],
    [t, list.sort],
  )

  const monthlyColumns = useMemo<ColumnsType<MonthlyReportRow>>(() => {
    const dateColumn: ColumnsType<MonthlyReportRow>[number] = {
      key: 'date',
      title: t('attendance.report.date'),
      width: 150,
      ...sortColumn(list.sort, 'date'),
      render: (_, row) => <Typography.Text>{formatDate(`${row.date}T00:00:00Z`)}</Typography.Text>,
    }

    // ⚠️ UMUMIY REJIM: qator — butun tashkilotning kuni. Kelish/ketish
    // vaqti bu yerda MA'NOSIZ (kimning kelishi?), shuning uchun ular
    // umuman chizilmaydi — bo'sh ustun "ma'lumot yo'q" bo'lib
    // o'qilardi, holbuki savolning o'zi qo'yilmagan.
    if (!personalMonth) {
      return [
        dateColumn,
        {
          key: 'employeeCount',
          title: t('attendance.report.employeeCount'),
          width: 150,
          ...sortColumn(list.sort, 'employeeCount'),
          render: (_, row) => formatNumber(row.employeeCount),
        },
        {
          key: 'complete',
          title: t('attendance.report.daysComplete'),
          width: 140,
          render: (_, row) => formatNumber(row.completeCount),
        },
        {
          key: 'incomplete',
          title: t('attendance.report.daysIncomplete'),
          width: 160,
          render: (_, row) => (
            <Tooltip title={t('attendance.report.daysIncompleteHint')}>
              <Typography.Text>{formatNumber(row.incompleteCount)}</Typography.Text>
            </Tooltip>
          ),
        },
        {
          key: 'absent',
          title: t('attendance.report.daysAbsent'),
          width: 140,
          ...sortColumn(list.sort, 'absent'),
          render: (_, row) => formatNumber(row.absentCount),
        },
        {
          key: 'denied',
          title: t('attendance.report.denied'),
          width: 120,
          render: (_, row) =>
            row.deniedCount ? formatNumber(row.deniedCount) : <Typography.Text type="secondary">{t('common.dash')}</Typography.Text>,
        },
      ]
    }

    // SHAXSIY REJIM: qator — bitta xodimning kuni, kunlik hisobotdagi
    // bilan bir xil savollar.
    return [
      dateColumn,
      {
        key: 'firstIn',
        title: t('attendance.report.firstIn'),
        width: 160,
        render: (_, row) => <TimeCell iso={row.firstInAt} local={row.firstInLocal} device={null} />,
      },
      {
        key: 'lastOut',
        title: t('attendance.report.lastOut'),
        width: 160,
        render: (_, row) => <TimeCell iso={row.lastOutAt} local={row.lastOutLocal} device={null} />,
      },
      {
        key: 'worked',
        title: t('attendance.report.worked'),
        width: 190,
        ...sortColumn(list.sort, 'worked'),
        render: (_, row) => <WorkedCell status={row.status} workedMinutes={row.workedMinutes} />,
      },
      {
        key: 'status',
        title: t('attendance.report.status'),
        width: 200,
        render: (_, row) => (
          <StatusCell
            status={row.status}
            nightShiftSuspected={row.nightShiftSuspected}
            note={row.note}
          />
        ),
      },
    ]
  }, [t, personalMonth, list.sort])

  const isDaily = view === 'daily'
  const activeQuery = isDaily ? daily : monthly
  const hasRows = isDaily ? dailyRows.length > 0 : monthlyRows.length > 0

  /*
    ⚠️ KUNLIK HISOBOTNING ENG MUHIM OGOHLANTIRISHI. Qurilma "yuz
    tanilmadi" hodisasida xodim raqamini UMUMAN yubormaydi, ya'ni bu
    urinishlarni hech kimga bog'lab bo'lmaydi. Natijada "KELMAGAN"
    qatorlari ishonchsiz bo'lishi mumkin: odam kelgan, terminal uni
    tanimagan, hisobotda esa u umuman kelmagandek ko'rinadi.
  */
  const dailyExtraNotices = useMemo(() => {
    const list: string[] = []
    const unidentified = daily.data?.unidentifiedDeniedCount ?? 0
    const unattributed = daily.data?.unattributedGrantedCount ?? 0
    if (unidentified > 0) {
      list.push(t('attendance.report.noticeUnidentified', { count: unidentified }))
    }
    if (unattributed > 0) {
      list.push(t('attendance.report.noticeUnattributed', { count: unattributed }))
    }
    return list
  }, [daily.data, t])

  const employeeOptions = useMemo(
    () => (employees.data ?? []).map((e) => ({ value: e.id, label: e.label })),
    [employees.data],
  )

  return (
    <>
      <PageHeader
        title={t('nav.attendanceReport')}
        description={t('pageIntro.attendanceReport')}
        extra={
          <Space wrap>
            <Segmented<View>
              value={view}
              onChange={setView}
              options={[
                { label: t('attendance.report.daily'), value: 'daily' },
                { label: t('attendance.report.monthly'), value: 'monthly' },
              ]}
            />
            {isDaily ? (
              <DatePicker
                value={day}
                onChange={(value) => {
                  if (value) list.setFilter('day', value.format('YYYY-MM-DD'))
                }}
                allowClear={false}
                format="DD.MM.YYYY"
              />
            ) : (
              <>
                <DatePicker
                  picker="month"
                  value={month}
                  onChange={(value) => {
                    if (value) list.setFilter('month', value.format('YYYY-MM'))
                  }}
                  allowClear={false}
                  format="MM.YYYY"
                />
                {/* ⚠️ Bo'sh qoldirish — HAQIQIY tanlov, "filtr qo'yilmagan"
                    emas: u butun tashkilot bo'yicha kunlik yig'indini
                    beradi. Shuning uchun placeholder aynan shuni aytadi. */}
                <Select<string | null>
                  value={employeeId}
                  onChange={(value) => list.setFilter('employee', value ?? null)}
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  loading={employees.isPending}
                  placeholder={t('attendance.report.employeeFilter')}
                  style={{ minWidth: 240 }}
                  options={employeeOptions}
                />
              </>
            )}
            <Tooltip title={t('attendance.report.exportHint')}>
              <Button
                icon={<DownloadOutlined />}
                disabled={!hasRows}
                onClick={() =>
                  void downloadAttendanceExport(
                    isDaily ? { date: dateParam } : { month: monthParam, employeeId },
                  )
                }
              >
                {t('attendance.report.export')}
              </Button>
            </Tooltip>
          </Space>
        }
      />

      {activeQuery.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('attendance.report.loadFailed')}
          description={translateError(activeQuery.error, t)}
        />
      ) : null}

      {isDaily && daily.data ? (
        <ReportNoticesAlert
          notices={daily.data.notices}
          extra={dailyExtraNotices}
          style={{ marginBottom: 16 }}
        />
      ) : null}
      {!isDaily && monthly.data ? (
        <ReportNoticesAlert notices={monthly.data.notices} style={{ marginBottom: 16 }} />
      ) : null}

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={12}>
          <PresentCard
            rows={present.data?.rows ?? []}
            windowHours={present.data?.windowHours ?? null}
            loading={present.isPending}
            error={present.isError ? translateError(present.error, t) : null}
          />
        </Col>
        <Col xs={24} lg={12}>
          <LastSeenCard
            rows={lastSeen.data?.rows ?? []}
            neverSeenCount={lastSeen.data?.neverSeenCount ?? null}
            loading={lastSeen.isPending}
            error={lastSeen.isError ? translateError(lastSeen.error, t) : null}
          />
        </Col>
      </Row>

      {/*
        ⚠️ KUNLIK YAKUN — JUFTLANMAGAN kunlar soni ALOHIDA ko'rsatiladi.

        U "tugallanmagan" bilan TENG EMAS: chiqish kirishdan oldin
        bo'lgan kun ham tugallanmagan, lekin u yerda ikkala yozuv ham
        bor. Faqat "tugallanmagan" sonini ko'rsatish ikkita butunlay
        boshqa vaziyatni bitta raqamga yig'ib yuborardi.

        ⚠️ Nol bo'lganda ham chiziladi: "0" — o'lchangan javob, kartaning
        yo'qligi esa savol qoldirardi.

        ⚠️ LEKIN `null` NOL EMAS. Sxemada bu maydonlar `nullish`
        (`attendanceApi.ts`), ya'ni "server bu sonni bermadi" degan holat
        HAQIQATDA bor. Ilgari bu yerda `?? 0` turardi va u aynan shuni
        "nol" ga aylantirardi: "Kelmaganlar: 0" degan yozuv davomat
        hisobotida ISHONCHLI javob bo'lib o'qiladi, holbuki hech narsa
        o'lchanmagan. Yuqoridagi `workedMinutes` allaqachon to'g'ri
        yozilgan edi — bu to'rttasi o'sha qorovulsiz qolgan edi.
      */}
      {isDaily && daily.data ? (
        <Card size="small" style={{ marginBottom: 16 }}>
          <Row gutter={[16, 16]}>
            <Col xs={12} md={6}>
              <Statistic
                title={t('attendance.report.daysComplete')}
                value={daily.data.completeCount ?? t('common.dash')}
              />
            </Col>
            <Col xs={12} md={6}>
              <Statistic
                title={t('attendance.report.daysIncomplete')}
                value={daily.data.incompleteCount ?? t('common.dash')}
              />
            </Col>
            <Col xs={12} md={6}>
              <Statistic
                title={t('attendance.report.unpairedCount')}
                value={daily.data.unpairedCount ?? t('common.dash')}
              />
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {t('attendance.report.unpairedHint')}
              </Typography.Text>
            </Col>
            <Col xs={12} md={6}>
              <Statistic
                title={t('attendance.report.daysAbsent')}
                value={daily.data.absentCount ?? t('common.dash')}
              />
            </Col>
          </Row>
        </Card>
      ) : null}

      {/* Oylik YAKUN — faqat oylik kesimda va faqat server bergan bo'lsa */}
      {!isDaily && monthly.data?.totals ? (
        <Card size="small" style={{ marginBottom: 16 }}>
          <Row gutter={[16, 16]}>
            <Col xs={12} md={6}>
              <Statistic
                title={t('attendance.report.totalWorked')}
                value={
                  monthly.data.totals.workedMinutes === null
                    ? t('common.dash')
                    : formatDuration(monthly.data.totals.workedMinutes * 60)
                }
              />
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {t('attendance.report.workedMonthHint')}
              </Typography.Text>
            </Col>
            <Col xs={12} md={6}>
              <Statistic
                title={t('attendance.report.daysComplete')}
                value={monthly.data.totals.completeDays ?? t('common.dash')}
              />
            </Col>
            <Col xs={12} md={6}>
              <Statistic
                title={t('attendance.report.daysIncomplete')}
                value={monthly.data.totals.incompleteDays ?? t('common.dash')}
              />
            </Col>
            <Col xs={12} md={6}>
              <Statistic
                title={t('attendance.report.nightShiftDays')}
                value={monthly.data.totals.nightShiftDays ?? t('common.dash')}
              />
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {t('attendance.report.nightShiftHint')}
              </Typography.Text>
            </Col>
          </Row>
        </Card>
      ) : null}

      {/*
        ⚠️ QIDIRUV VA FILTR JADVAL TEPASIDA, ustun voronkasi ichida
        EMAS: ko'rinmagan boshqaruv mavjud emas bilan barobar (etalon
        jadvalda o'lchangan).

        ⚠️ Umumiy OYLIK kesimda ikkalasi ham CHIZILMAYDI va bu ataylab:
        u yerda qator — butun tashkilotning kuni, ya'ni izlanadigan ism
        ham, bitta holat ham yo'q. Ishlamaydigan maydonni ko'rsatish
        "qidiruv buzuq" degan xulosa berardi.
      */}
      {isDaily || personalMonth ? (
        <Flex wrap gap="small" align="center" style={{ marginBottom: 12 }}>
          {isDaily ? (
            <Input.Search
              allowClear
              value={list.searchText}
              onChange={(e) => list.setSearchText(e.target.value)}
              placeholder={t('attendance.report.searchPlaceholder')}
              style={{ width: 260, maxWidth: '100%' }}
            />
          ) : null}
          <Select<string>
            allowClear
            value={list.filter('status')}
            onChange={(value) => list.setFilter('status', value ?? null)}
            placeholder={t('attendance.report.status')}
            style={{ width: 190 }}
            options={DAY_STATUSES.map((status) => ({
              value: status,
              label: t(`attendance.report.statuses.${status}`),
            }))}
          />
          {isDaily ? (
            <Select<string>
              allowClear
              value={list.filter('registry')}
              onChange={(value) => list.setFilter('registry', value ?? null)}
              placeholder={t('attendance.report.notInRegistry')}
              style={{ width: 220 }}
              // «Reyestrda yo'q: Ha / Yo'q» — savol placeholder'da,
              // javob variantda. Ikkalasini ham matn qilish
              // («Reyestrda yo'q» / «Reyestrda bor») tanlovni ikki
              // marta o'qishga majburlardi.
              options={[
                { value: 'missing', label: t('common.yes') },
                { value: 'present', label: t('common.no') },
              ]}
            />
          ) : null}
          {list.narrowed ? (
            <>
              <Typography.Text type="secondary">
                {t('listView.shown', {
                  shown: isDaily ? visibleDaily.length : visibleMonthly.length,
                  total: isDaily ? dailyRows.length : monthlyRows.length,
                })}
              </Typography.Text>
              <Button type="link" size="small" onClick={list.reset}>
                {t('dataTable.clearFilters')}
              </Button>
            </>
          ) : null}
        </Flex>
      ) : null}

      {/* ⚠️ Filtr YAKUNGA tegmasligi OCHIQ aytiladi: yuqoridagi
          kartochkalar serverning butun kunlik yig'indisi va ular
          jadval bilan mos kelmasligi MUMKIN. Aytilmasa operator
          "yig'indi noto'g'ri" degan xulosaga kelardi. */}
      {list.narrowed ? (
        <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
          {t('attendance.report.filterScopeNote')}
        </Typography.Paragraph>
      ) : null}

      {isDaily ? (
        <Table<DailyReportRow>
          columns={dailyColumns}
          dataSource={[...visibleDaily]}
          rowKey={(row) => row.employeeId ?? row.employeeNo}
          loading={daily.isFetching}
          pagination={false}
          size="middle"
          scroll={{ x: 980 }}
          onChange={localSortChange<DailyReportRow>(list.patch)}
          locale={{
            emptyText:
              // ⚠️ «Hisobotda qator yo'q» va «filtr hech narsa topmadi»
              //    IKKI XIL javob. Ikkinchisida yagona to'g'ri harakat
              //    filtrni tozalash, birinchisida esa boshqa kun tanlash.
              dailyRows.length > 0 ? (
                <EmptyState
                  variant="no-data"
                  title={t('listView.noMatch')}
                  description={t('listView.noMatchHint', { total: dailyRows.length })}
                  actionLabel={t('dataTable.clearFilters')}
                  onAction={list.reset}
                />
              ) : (
                <EmptyState variant="no-data" title={t('attendance.report.empty')} />
              ),
          }}
        />
      ) : (
        <Table<MonthlyReportRow>
          columns={monthlyColumns}
          dataSource={[...visibleMonthly]}
          rowKey={(row) => row.date}
          loading={monthly.isFetching}
          pagination={false}
          size="middle"
          scroll={{ x: 900 }}
          onChange={localSortChange<MonthlyReportRow>(list.patch)}
          locale={{
            emptyText:
              monthlyRows.length > 0 ? (
                <EmptyState
                  variant="no-data"
                  title={t('listView.noMatch')}
                  description={t('listView.noMatchHint', { total: monthlyRows.length })}
                  actionLabel={t('dataTable.clearFilters')}
                  onAction={list.reset}
                />
              ) : (
                <EmptyState variant="no-data" title={t('attendance.report.empty')} />
              ),
          }}
        />
      )}

      <Typography.Paragraph type="secondary" style={{ marginTop: 16 }}>
        {t('attendance.report.note')}
      </Typography.Paragraph>
      {/* ⚠️ NEGA bu yerda «oxirgi qo'shilgan birinchi» YO'Q — sabab
          yozib qo'yiladi, aks holda u unutilgan qoida bo'lib
          ko'rinardi. */}
      <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
        {`${t('attendance.report.orderNote')} ${t('listView.fullListNote')}`}
      </Typography.Paragraph>
    </>
  )
}

/**
 * Vaqt yoki chiziqcha.
 *
 * ⚠️ Tooltip'da UCH manba: UTC + zonalar (`utcTooltip`), SERVER hisobot
 * zonasida tayyorlagan matn (`firstInLocal`) va qaysi TERMINAL yozgani.
 * Ikkinchisi bezak emas — hisobot kun chegarasi qurilma zonasi bo'yicha
 * hisoblanadi, ya'ni server matni panelnikidan farq qilsa, farqning
 * o'zi topilma bo'ladi (2026-08-11 dagi zona nosozligi bilan bir xil
 * turdagi xato).
 */
function TimeCell({
  iso,
  local,
  device,
}: {
  iso: string | null
  local: string | null
  device: string | null
}) {
  const { t } = useTranslation()

  if (iso === null) return <Typography.Text type="secondary">—</Typography.Text>

  const lines = utcTooltip(iso).split('\n')
  if (local) lines.push(`${t('attendance.report.serverLocal')}: ${local}`)
  if (device) lines.push(`${t('attendance.report.device')}: ${device}`)

  return (
    <Tooltip
      title={lines.map((line, index) => (
        <div key={index}>{line}</div>
      ))}
    >
      <Typography.Text>{formatDateTime(iso)}</Typography.Text>
    </Tooltip>
  )
}

/**
 * Ishlagan vaqt.
 *
 * ⚠️ BU KOMPONENT SAHIFANING ASOSIY QOIDASINI QO'LLAYDI: tugallanmagan
 * kunda "0 soat" YOZILMAYDI. Chiziqcha turadi va tooltip NEGA ekanini
 * aytadi — ya'ni operator raqamning yo'qligini nosozlik deb o'ylamaydi
 * va uni qidirib vaqt sarflamaydi.
 *
 * Kelmagan kunda ham chiziqcha, lekin BOSHQA izoh bilan: u yerda vaqt
 * noma'lum emas, umuman yo'q. Ikkalasiga bir xil matn yozish ularni
 * chalkashtirardi.
 *
 * ⚠️ `workedMinutes` DAQIQADA keladi (`Integer workedMinutes`), shuning
 * uchun soniyaga ko'paytiriladi — `formatDuration` soniya kutadi.
 * Ko'paytirishni unutish 8 soatlik kunni "8 daqiqa" qilib ko'rsatardi.
 */
function WorkedCell({
  status,
  workedMinutes,
}: {
  /**
   * ⚠️ `null` — UMUMIY rejim: bir kunning yagona holati yo'q
   * (`MonthlyReportRow.status`). Bugun bu shox EKRANGA CHIQMAYDI —
   * umumiy rejimda «Ishlangan» ustuni umuman chizilmaydi — lekin tip
   * haqiqatni aytadi va ustun qo'shilgan kunda kompilyator shu yerni
   * ko'rsatadi.
   */
  status: DayStatus | null
  workedMinutes: number | null
}) {
  const { t } = useTranslation()

  if (status === 'COMPLETE' && workedMinutes !== null) {
    return <Typography.Text>{formatDuration(workedMinutes * 60)}</Typography.Text>
  }

  const absent = status === 'ABSENT'

  return (
    <Tooltip
      title={t(absent ? 'attendance.report.workedAbsentHint' : 'attendance.report.workedUnknownHint')}
    >
      <Space size={6}>
        <Typography.Text type="secondary">—</Typography.Text>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {t(absent ? 'attendance.report.workedAbsent' : 'attendance.report.workedUnknown')}
        </Typography.Text>
      </Space>
    </Tooltip>
  )
}

/**
 * Holat + TUNGI SMENA gumoni.
 *
 * ⚠️ Tungi smena belgisi jimgina tashlab yuborilmasligi kerak. Kun
 * chegarasi yarim tunda o'tadi, ya'ni tunda ishlagan odamning bitta
 * smenasi IKKI kunga bo'linadi va ikkalasi ham "tugallanmagan" bo'lib
 * ko'rinadi. Belgisiz operator mavjud bo'lmagan intizom buzilishini
 * "topardi" — va bu xulosaga qarshi hech qanday dalil ekranda
 * bo'lmasdi.
 */
function StatusCell({
  status,
  nightShiftSuspected,
  note,
  directionSource,
  unpaired,
}: {
  /** ⚠️ `null` — umumiy rejim (`WorkedCell` dagi bir xil izoh) */
  status: DayStatus | null
  nightShiftSuspected: boolean
  note: string | null
  /**
   * ⚠️ Qatorning ISHONCHLILIK darajasi. `undefined` — oylik jadval
   * (u yerda manba qaytmaydi va ustun chizilmaydi).
   */
  directionSource?: DirectionSource
  unpaired?: boolean
}) {
  const { t } = useTranslation()

  return (
    <Space size={4} wrap>
      {/* ⚠️ Holat YO'Q bo'lsa XOM KALIT chizilmaydi
          (`attendance.report.statuses.null`) — neytral chiziqcha */}
      {status === null ? (
        <Typography.Text type="secondary">{t('common.dash')}</Typography.Text>
      ) : (
        <Tooltip title={t(`attendance.report.statusHint.${status}`)}>
          <Tag color={STATUS_COLOR[status]}>{t(`attendance.report.statuses.${status}`)}</Tag>
        </Tooltip>
      )}

      {/*
        ⚠️⚠️ MANBA YORLIG'I — "bu son o'lchovmi yoki taxminmi".

        Faqat `DIRECTION` bo'lmaganda chiziladi va bu ataylab: o'lchov
        NORMA bo'lishi kerak, taxmin esa ko'zga tashlanishi. Har qatorda
        yorliq bo'lsa ("O'LCHOV"/"TAXMIN") ikkalasi ham shovqinga
        aylanardi va farq yo'qolardi.

        ⚠️ `ABSENT` qatorda ko'rsatilmaydi: hodisa umuman yo'q, ya'ni
        o'lchanadigan narsa ham yo'q va yorliq faqat chalkashtirardi.
      */}
      {directionSource !== undefined
      && directionSource !== 'DIRECTION'
      && status !== 'ABSENT' ? (
        <Tooltip title={t(`attendance.report.sourceHint.${directionSource}`)}>
          <Tag color="purple">{t(`attendance.report.sources.${directionSource}`)}</Tag>
        </Tooltip>
      ) : null}

      {/*
        ⚠️ JUFTLANMAGAN — "kirdi, chiqmadi". Bu NOSOZLIK EMAS va
        shuning uchun rang QIZIL emas: qizil yorliq operatorni
        tuzatilishi kerak bo'lgan xato qidirishga yuborardi, holbuki
        odam chiqishda terminaldan o'tmasligi butunlay odatiy.
      */}
      {unpaired ? (
        <Tooltip title={t('attendance.report.unpairedHint')}>
          <Tag color="gold">{t('attendance.report.unpaired')}</Tag>
        </Tooltip>
      ) : null}

      {nightShiftSuspected ? (
        <Tooltip title={t('attendance.report.nightShiftHint')}>
          <Tag color="blue">{t('attendance.report.nightShift')}</Tag>
        </Tooltip>
      ) : null}
      {/* Server izohi XOM holda — u vaziyatni bizdan yaxshi biladi */}
      {note ? (
        <Tooltip title={note}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            ⓘ
          </Typography.Text>
        </Tooltip>
      ) : null}
    </Space>
  )
}

/**
 * "Hozir ichkarida".
 *
 * ⚠️ IZOH BEZAK EMAS. Terminal kirish va chiqish YO'NALISHINI umuman
 * aytmaydi — u faqat "tanildi" deydi, ro'yxat esa yozuvlar juft-toqligi
 * bo'yicha chiqariladi. Ya'ni bu O'LCHOV emas, XULOSA.
 *
 * Eng ehtimolli noto'g'ri qo'llanish — uni evakuatsiya ro'yxati sifatida
 * ishlatish. Chiqishda terminalga yaqinlashmagan odam kechgacha shu
 * ro'yxatda turadi, ya'ni ro'yxat odamlarni binoda yo'q bo'lsa ham
 * ko'rsatadi. Izoh aynan shuning uchun jadval TEPASIDA va matni ochiq.
 */
function PresentCard({
  rows,
  windowHours,
  loading,
  error,
}: {
  rows: readonly PresentRow[]
  windowHours: number | null
  loading: boolean
  error: string | null
}) {
  const { t } = useTranslation()

  return (
    <Card
      size="small"
      title={t('attendance.report.presentTitle')}
      loading={loading}
      style={{ height: '100%' }}
    >
      <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
        {t('attendance.report.presentHint')}
      </Typography.Paragraph>
      {windowHours !== null ? (
        <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
          {t('attendance.report.presentWindow', { hours: windowHours })}
        </Typography.Paragraph>
      ) : null}

      {error ? (
        <Alert type="error" showIcon message={error} />
      ) : rows.length === 0 ? (
        <Typography.Text type="secondary">{t('attendance.report.presentEmpty')}</Typography.Text>
      ) : (
        <Space direction="vertical" size={6} style={{ width: '100%' }}>
          {rows.slice(0, 10).map((row) => (
            <Space key={row.employeeNo} size={8} wrap>
              <Typography.Text>{row.displayName ?? row.employeeNo}</Typography.Text>
              <Typography.Text code style={{ fontSize: 12 }}>
                {row.employeeNo}
              </Typography.Text>
              {/*
                ⚠️ TAXMIN QILINGAN QATOR BELGILANADI, o'lchangani esa
                YO'Q. Ro'yxatda ikkalasi aralash bo'lishi odatiy (bir
                eshikda yo'nalish bor, boshqasida yo'q) va belgisiz
                operator butun ro'yxatni bir xil ishonchli deb o'qirdi.
              */}
              {row.source === 'DIRECTION' ? null : (
                <Tooltip title={t('attendance.report.sourceHint.ORDER')}>
                  <Tag color="purple">{t('attendance.report.sources.ORDER')}</Tag>
                </Tooltip>
              )}
              {row.zone ? (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {row.zone}
                </Typography.Text>
              ) : null}
              {row.sinceAt ? (
                <Tooltip title={utcTooltip(row.sinceAt)}>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {row.minutesInside === null
                      ? relativeFromNow(row.sinceAt, t)
                      : t('attendance.report.insideFor', {
                          duration: formatDuration(row.minutesInside * 60),
                        })}
                  </Typography.Text>
                </Tooltip>
              ) : null}
            </Space>
          ))}
          {rows.length > 10 ? (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {t('attendance.report.andMore', { count: rows.length - 10 })}
            </Typography.Text>
          ) : null}
        </Space>
      )}
    </Card>
  )
}

/**
 * "Oxirgi ro'yxatdan o'tish".
 *
 * ⚠️ USUL MATNI SERVERDAN (`methodLabel`), `verifyMode.ts` DAN EMAS.
 * Bu endpoint xom `minor` kodini umuman bermaydi — faqat tasniflangan
 * `method`/`methodLabel` ni beradi. O'z lug'atimizni bu yerda ham
 * ishlatish ikki joyda ikki xil nom berish xavfini tug'dirardi ("Yuz"
 * va "Yuz tanish" bir xil narsa uchun). Qoida: server bergan joyda
 * SERVERNIKI, bermagan joyda (hodisalar jurnali) o'zimizniki.
 *
 * ⚠️ `neverSeenCount` ALOHIDA ko'rsatiladi. Hech qachon ko'rinmagan
 * xodimlar ro'yxatda YO'Q — ko'rsatadigan vaqtlari yo'q. Ya'ni bu son
 * bo'lmasa ular butunlay ko'rinmas bo'lardi, holbuki "hech qachon
 * kelmagan xodim" aynan shu blokda qidiriladigan holat.
 */
function LastSeenCard({
  rows,
  neverSeenCount,
  loading,
  error,
}: {
  rows: readonly LastSeenRow[]
  neverSeenCount: number | null
  loading: boolean
  error: string | null
}) {
  const { t } = useTranslation()

  return (
    <Card
      size="small"
      title={t('attendance.report.lastSeenTitle')}
      loading={loading}
      style={{ height: '100%' }}
    >
      <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
        {t('attendance.report.lastSeenHint')}
      </Typography.Paragraph>

      {neverSeenCount !== null && neverSeenCount > 0 ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={t('attendance.report.neverSeen', { count: neverSeenCount })}
        />
      ) : null}

      {error ? (
        <Alert type="error" showIcon message={error} />
      ) : rows.length === 0 ? (
        <Typography.Text type="secondary">{t('attendance.report.lastSeenEmpty')}</Typography.Text>
      ) : (
        <Space direction="vertical" size={6} style={{ width: '100%' }}>
          {rows.slice(0, 10).map((row) => (
            <Space key={row.employeeNo} size={8} wrap>
              <Typography.Text>{row.displayName ?? row.employeeNo}</Typography.Text>
              <Typography.Text code style={{ fontSize: 12 }}>
                {row.employeeNo}
              </Typography.Text>
              {row.methodLabel ? (
                <Tag style={{ marginInlineEnd: 0 }}>{row.methodLabel}</Tag>
              ) : null}
              {row.deviceName ? (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {row.deviceName}
                </Typography.Text>
              ) : null}
              {row.lastSeenAt ? (
                <Tooltip title={utcTooltip(row.lastSeenAt)}>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {relativeFromNow(row.lastSeenAt, t)}
                  </Typography.Text>
                </Tooltip>
              ) : null}
            </Space>
          ))}
          {rows.length > 10 ? (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {t('attendance.report.andMore', { count: rows.length - 10 })}
            </Typography.Text>
          ) : null}
        </Space>
      )}
    </Card>
  )
}
